import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, AlertCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import FormContainer from '../../components/ui/FormContainer';
import Button from '../../components/ui/Button';
import DocumentUploadSection from './components/DocumentUploadSection';
import UploadHeader from './components/UploadHeader';
import useIsMounted from '../../hooks/useIsMounted';
import { loadFormData, saveProgress, getUserEmail, getLocationId } from '../../utils/onboardingApi';

const DocumentUploadStep2 = () => {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const saveTimeoutRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [uploadedFiles, setUploadedFiles] = useState({
    ppm: null,
    pitchDeck: null,
    proformaXls: null
  });
  const [validationError, setValidationError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [driveUploadComplete, setDriveUploadComplete] = useState(false);


  // Load saved form data from API on component mount
  useEffect(() => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.warn('No email found. User may need to access via email link.');
      setIsLoading(false);
      return;
    }

    // Try to load from API
    loadFormData('documents')
      .then(data => {
        if (Object.keys(data).length > 0 && isMounted.current) {
          setUploadedFiles(prev => ({
            ...prev,
            ppm: data?.ppm || null,
            pitchDeck: data?.pitchDeck || null,
            proformaXls: data?.proformaXls || null
          }));
          // Check if loaded data has confirmed Drive uploads (fileId present)
          const docKeys = ['ppm', 'pitchDeck', 'proformaXls'];
          const confirmedCount = docKeys.filter(k => data?.[k]?.fileId).length;
          if (confirmedCount >= 2) {
            setDriveUploadComplete(true);
          }
        } else {
          // Fallback to localStorage for legacy data
          const savedData = localStorage.getItem('dealFormData');
          if (savedData && isMounted.current) {
            try {
              const parsed = JSON.parse(savedData);
              setUploadedFiles(prev => ({
                ...prev,
                ppm: parsed?.ppm || null,
                pitchDeck: parsed?.pitchDeck || null,
                proformaXls: parsed?.proformaXls || null
              }));
            } catch (e) {
              console.error('Failed to parse localStorage data:', e);
            }
          }
        }
      })
      .catch(err => {
        console.error('Error loading form data:', err);
        // Fallback to localStorage on API error
        const savedData = localStorage.getItem('dealFormData');
        if (savedData && isMounted.current) {
          try {
            const parsed = JSON.parse(savedData);
            setUploadedFiles(prev => ({
              ...prev,
              ppm: parsed?.ppm || null,
              pitchDeck: parsed?.pitchDeck || null,
              proformaXls: parsed?.proformaXls || null
            }));
          } catch (e) {
            console.error('Failed to parse localStorage data:', e);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isMounted]);

  // Helper to serialize a File (or pass-through existing serialized object)
  const serializeFile = (file) => {
    if (!file) return null;
    if (file && typeof file === 'object' && 'name' in file && 'size' in file && !('arrayBuffer' in file)) {
      // Likely already serialized metadata object
      return file;
    }
    return {
      name: file?.name,
      size: file?.size,
      type: file?.type || (file?.name?.split('.')?.pop() || ''),
      lastModified: file?.lastModified || Date.now()
    };
  };

  // Auto-save document data to API whenever it changes
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load

    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (!isMounted.current) return;

      // Build clean flat object
      const flatDocs = Object.keys(uploadedFiles).reduce((acc, key) => {
        const file = uploadedFiles?.[key];
        acc[key] = file ? serializeFile(file) : null;
        return acc;
      }, {});

      saveProgress('documents', flatDocs)
        .then(() => {})
        .catch(err => {
          console.error('Failed to auto-save:', err);
          // Fallback to localStorage on API error
          localStorage.setItem('dealFormData_backup', JSON.stringify(flatDocs));
        });
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(saveTimeoutRef.current);
  }, [uploadedFiles, isMounted, isLoading]);

  const documentTypes = [
    {
      id: 'ppm',
      label: 'PPM',
      description: 'Private Placement Memorandum',
      acceptedTypes: '.pdf'
    },
    {
      id: 'pitchDeck',
      label: 'Pitch Deck',
      description: 'Presentation slides',
      acceptedTypes: '.pdf'
    },
    {
      id: 'proformaXls',
      label: 'Proforma XLS',
      description: 'Financial projections spreadsheet',
      acceptedTypes: '.xls,.xlsx,.csv'
    }
  ];

  const handleFileUpload = (documentType, file) => {
    if (!isMounted.current) return;

    setUploadedFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
    // New file selected — needs uploading
    setDriveUploadComplete(false);
    // Clear validation error when user uploads a file
    if (validationError) {
      setValidationError('');
    }
  };

  const handleFileRemove = (documentType) => {
    if (!isMounted.current) return;
    setDriveUploadComplete(false);

    setUploadedFiles(prev => ({
      ...prev,
      [documentType]: null
    }));
  };

  const validateRequiredDocuments = () => {
    const uploadedCount = Object.values(uploadedFiles)?.filter(file => file !== null)?.length;
    const hasCaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    // Only require captcha if the site key is configured
    return uploadedCount >= 2 && (!hasCaptchaKey || captchaToken !== null);
  };

  const getUploadedCount = () => {
    return Object.values(uploadedFiles)?.filter(file => file !== null)?.length;
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    // Clear validation error when user completes CAPTCHA
    if (validationError) {
      setValidationError('');
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleBack = () => {
    navigate('/deal-information-form-step-2');
  };

  const uploadWithRetry = async (attempt = 1) => {
    const maxAttempts = 2;

    try {
      if (attempt === 1) {
        setUploadProgress('Uploading your documents...');
      } else {
        setUploadProgress(`Retrying upload (attempt ${attempt} of ${maxAttempts})...`);
      }

      // Upload files to Railway API
      const formData = new FormData();
      const locationId = getLocationId();

      if (!locationId) {
        throw new Error('Location ID is required for file upload');
      }

      formData.append('locationId', locationId);
      formData.append('folderPath', 'Inbox');

      // Add all uploaded files to FormData
      Object.entries(uploadedFiles).forEach(([key, file]) => {
        if (file && file instanceof File) {
          formData.append('files', file);
        }
      });

      const response = await fetch('https://raiseai-api-production.up.railway.app/api/media/upload-to-drive', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Map uploaded file URLs back to document types and merge with existing confirmed uploads
      const confirmedDocs = {};
      // Preserve any previously confirmed uploads
      Object.entries(uploadedFiles).forEach(([key, file]) => {
        if (file && file.fileId) {
          confirmedDocs[key] = file;
        }
      });
      // Add newly uploaded files
      let fileIndex = 0;
      Object.entries(uploadedFiles).forEach(([key, file]) => {
        if (file && file instanceof File) {
          const uploadResult = result.results?.[fileIndex];
          if (uploadResult?.success) {
            confirmedDocs[key] = {
              name: file.name,
              size: file.size,
              type: file.type,
              url: uploadResult.url,
              fileId: uploadResult.fileId,
              filename: uploadResult.filename
            };
          }
          fileIndex++;
        }
      });

      // Save confirmed uploads back to API (scoped to this user/location)
      await saveProgress('documents', confirmedDocs);

      // Update state so returning to this page knows these are confirmed
      setUploadedFiles(prev => ({ ...prev, ...confirmedDocs }));
      setDriveUploadComplete(true);

      // Also update localStorage for backwards compat
      const existingData = JSON.parse(localStorage.getItem('dealFormData') || '{}');
      localStorage.setItem('dealFormData', JSON.stringify({ ...existingData, ...confirmedDocs }));
      setUploadProgress('Upload complete!');

      // Reset CAPTCHA after successful upload
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // Navigate to brand guidelines setup
      setTimeout(() => {
        if (isMounted.current) {
          navigate('/brand-guidelines-setup');
        }
      }, 500);

    } catch (error) {
      console.error('File upload error:', error);

      if (attempt < maxAttempts) {
        // Retry after 3 seconds
        setRetryCount(attempt);
        setUploadProgress(`Upload failed. Retrying in 3 seconds...`);

        await new Promise(resolve => setTimeout(resolve, 3000));

        if (isMounted.current) {
          return uploadWithRetry(attempt + 1);
        }
      } else {
        // Final failure after retry
        setUploadError('Upload failed after retry. Please try again.');
        setUploadProgress('');
        throw error;
      }
    }
  };

  const handleContinue = async () => {
    if (!isMounted.current) return;

    // Validate that at least two documents are uploaded
    if (getUploadedCount() < 2) {
      setValidationError('Please upload at least two documents to continue.');
      return;
    }

    // Only require captcha if site key is configured
    if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && !captchaToken) {
      setValidationError('Please complete the CAPTCHA verification.');
      return;
    }

    // If documents were already successfully uploaded to Drive, skip re-upload
    if (driveUploadComplete) {
      // Check if user added any new files that need uploading
      const hasNewFiles = Object.values(uploadedFiles).some(f => f instanceof File);
      if (!hasNewFiles) {
        navigate('/brand-guidelines-setup');
        return;
      }
    }

    // Check for stale metadata - files that aren't File objects and weren't confirmed uploaded
    const hasStaleFiles = Object.entries(uploadedFiles).some(([key, file]) => {
      if (!file) return false;
      if (file instanceof File) return false;
      // It's metadata - only OK if it has a fileId (confirmed uploaded to Drive)
      return !file.fileId;
    });

    if (hasStaleFiles) {
      setValidationError('Some documents need to be re-selected. Please remove and re-upload them.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadProgress('');
    setRetryCount(0);
    try {
      // Save document metadata to API before uploading files
      const flatDocs = Object.keys(uploadedFiles).reduce((acc, key) => {
        const file = uploadedFiles?.[key];
        acc[key] = file ? serializeFile(file) : null;
        return acc;
      }, {});

      await saveProgress('documents', flatDocs);

      // Then proceed with file upload
      await uploadWithRetry();

    } catch (error) {
      // Error already handled in uploadWithRetry
    } finally {
      if (isMounted.current) {
        setIsUploading(false);
      }
    }
  };

  const uploadedCount = getUploadedCount();

  return (
    <FormContainer maxWidth="max-w-2xl">
      <div className="space-y-8">
        {/* Header */}
        <UploadHeader onBack={handleBack} />

        {/* Requirement Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Document Requirements
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Please upload at least two documents to proceed. ({uploadedCount}/3 uploaded)
              </p>
            </div>
          </div>
        </div>

        {/* Upload Sections */}
        <div className="space-y-6">
          {documentTypes?.map((docType) => (
            <DocumentUploadSection
              key={docType?.id}
              documentType={docType}
              uploadedFile={uploadedFiles?.[docType?.id]}
              onFileUpload={(file) => handleFileUpload(docType?.id, file)}
              onFileRemove={() => handleFileRemove(docType?.id)}
            />
          ))}
        </div>

        {/* reCAPTCHA - Only show if site key is configured */}
        {import.meta.env.VITE_RECAPTCHA_SITE_KEY && (
          <div className="flex justify-center py-4">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              onExpired={handleCaptchaExpired}
              theme="light"
            />
          </div>
        )}

        {/* Validation Error */}
        {validationError && (
          <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" />
            <span>{validationError}</span>
          </div>
        )}

        {/* Upload Error */}
        {uploadError && (
          <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" />
            <span>{uploadError}</span>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && uploadProgress && (
          <div className="flex items-center space-x-3 text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>{uploadProgress}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-6">
          <Button
            onClick={handleContinue}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            size="lg"
            disabled={!validateRequiredDocuments() || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Continue'}
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default DocumentUploadStep2;
